import { useEffect, useState } from 'react';
import { ArrowUpDown, Info, SearchX } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { cn, formatDateTime } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/status-badge';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DatePicker, civilFromInstant, instantFromCivil } from '@/components/ui/date-picker';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { defaultCardReconciliationRange } from '@/lib/reconciliation-default-range';
import { useLocationStore } from '@/stores/location-store';
import {
  useSupernovaMatched,
  useSupernovaUnmatchedSource,
  useSupernovaUnmatchedFunding,
  useSupernovaAmountMismatch,
  useSupernovaDuplicateTransactions,
  useSupernovaOrders,
  useSupernovaExcluded,
  useSupernovaVoided,
  useSupernovaVoidedRecaptured,
  useSupernovaItems,
  useSupernovaPayments,
  useSupernovaSummary,
  type SupernovaMatchedRow,
  type SupernovaUnmatchedSourceRow,
  type SupernovaUnmatchedFundingRow,
  type SupernovaDuplicatePaymentRow,
  type SupernovaOrderRow,
  type SupernovaItemRow,
  type SupernovaFundedRow,
  type SupernovaPagination,
} from '@/hooks/use-supernova-reconciliation';

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const formatted = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return num < 0 ? `-$${formatted}` : `$${formatted}`;
}

// Bridge the string 'YYYY-MM-DD' filter state to the DatePicker's Date value.
// Both directions resolve the day in the *reporting* zone — the same zone the
// DatePicker renders in — so the displayed date matches the picked day
// regardless of the browser timezone. Building the Date in browser-local time
// (the old behaviour) shifted the display back a day whenever the browser zone
// was ahead of the reporting zone (e.g. IST vs Pacific), while the query — which
// uses the tz-neutral 'YYYY-MM-DD' string — stayed correct.
const ymdToDate = (s: string, tz: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return y && m && d ? instantFromCivil({ year: y, month: m, day: d, hour: 0, minute: 0, second: 0 }, tz) : null;
};
const dateToYmd = (d: Date | null, tz: string): string => {
  if (!d) return '';
  const c = civilFromInstant(d, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${p(c.month)}-${p(c.day)}`;
};

type SupernovaTab =
  | 'unmatched-source'
  | 'unmatched-funding'
  | 'amount-mismatch'
  | 'duplicate-transactions'
  | 'orders'
  | 'excluded'
  | 'voided'
  | 'voided-recaptured'
  | 'items'
  | 'payments'
  | 'matched';

const SUPERNOVA_TABS: { id: SupernovaTab; label: string; help?: string }[] = [
  {
    id: 'unmatched-source',
    label: 'Unmatched in POS',
    help: 'Transactions processed through the POS but not found in funding records.',
  },
  {
    id: 'unmatched-funding',
    label: 'Unmatched Backend - Funding',
    help: 'Transactions found in funding records but not found in the POS; no Order ID is available.',
  },
  {
    id: 'amount-mismatch',
    label: 'Amount Mismatch',
    help: 'Transactions where POS and funding amounts do not match.',
  },
  {
    id: 'duplicate-transactions',
    label: 'Duplicate - In POS',
    help: 'Transactions with the same card and amount processed within one minute.',
  },
  {
    id: 'voided',
    label: 'Voided',
    help: 'Transactions reversed before batch settlement and not processed again.',
  },
  {
    id: 'voided-recaptured',
    label: 'Voided But Recaptured',
    help: 'Transactions reversed before batch settlement and subsequently processed again.',
  },
  // 'orders' (raw POS-transactions-with-card-details listing) is
  // intentionally hidden from the tab bar. Kept in the type/sort-options/
  // query wiring below so it's a one-line re-add if needed.
  // 'excluded' (orders outside the final-sale statuses, including Voided's
  // refunded/partial_refund plus void/held/recalled) is intentionally
  // hidden from the tab bar. Kept in the type/sort-options/query wiring
  // below so it's a one-line re-add if needed.
  // 'items' (pos.transaction_items raw listing) is intentionally hidden from
  // the tab bar — Orders now covers the order-level breakdown reconciliation
  // needs, and Items duplicated that without adding much. Kept in the type/
  // sort-options/query wiring below so it's a one-line re-add if needed.
  { id: 'payments', label: 'Funded Transactions', help: 'Transactions successfully processed and sent to the bank.' },
  {
    id: 'matched',
    label: 'Matched POS to Funding',
    help: 'Transactions successfully matched between POS and funding records.',
  },
];

function SupernovaSummaryStat({
  label,
  count,
  total,
  accent,
}: {
  label: string;
  count?: number;
  total?: string;
  accent?: boolean;
}) {
  return (
    <div>
      <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
      <p className={cn('mt-1 text-xl font-semibold', accent && 'text-amber-600')}>{count ?? '—'}</p>
      <p className="text-sm text-muted-foreground">{total != null ? formatCurrency(total) : ''}</p>
    </div>
  );
}

const SUPERNOVA_SORT_OPTIONS: Record<SupernovaTab, { value: string; label: string }[]> = {
  matched: [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'transactionId', label: 'Transaction ID' },
    { value: 'authCode', label: 'Auth Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'paymentAmount', label: 'Payment Amount' },
    { value: 'fundingAmount', label: 'Funding Amount' },
    { value: 'fundingDate', label: 'Funding Date' },
  ],
  'unmatched-source': [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'transactionId', label: 'Transaction ID' },
    { value: 'authCode', label: 'Auth Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'itemCount', label: 'Item Count' },
    { value: 'paymentAmount', label: 'Payment Amount' },
  ],
  'unmatched-funding': [
    { value: 'fundingDate', label: 'Funding Date' },
    { value: 'transactionNumber', label: 'Transaction #' },
    { value: 'authCode', label: 'Auth Code' },
    { value: 'lastFour', label: 'Card' },
    { value: 'amount', label: 'Amount' },
    { value: 'orderId', label: 'Order Id (Funding File)' },
  ],
  'amount-mismatch': [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'transactionId', label: 'Transaction ID' },
    { value: 'authCode', label: 'Auth Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'paymentAmount', label: 'Payment Amount' },
    { value: 'fundingAmount', label: 'Funding Amount' },
    { value: 'variance', label: 'Variance' },
  ],
  'duplicate-transactions': [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'transactionId', label: 'Transaction ID' },
    { value: 'authCode', label: 'Auth Code' },
    { value: 'cardLastFour', label: 'Card' },
    { value: 'amount', label: 'Amount' },
  ],
  orders: [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'id', label: 'Transaction ID' },
    { value: 'total', label: 'Total' },
    { value: 'status', label: 'Status' },
  ],
  excluded: [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'id', label: 'Transaction ID' },
    { value: 'total', label: 'Total' },
    { value: 'status', label: 'Status' },
  ],
  voided: [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'id', label: 'Transaction ID' },
    { value: 'total', label: 'Total' },
    { value: 'status', label: 'Status' },
  ],
  'voided-recaptured': [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'id', label: 'Transaction ID' },
    { value: 'total', label: 'Total' },
    { value: 'status', label: 'Status' },
  ],
  items: [
    { value: 'createdAt', label: 'Order Date' },
    { value: 'dailyOrderNumber', label: 'Order #' },
    { value: 'transactionId', label: 'Transaction ID' },
    { value: 'productId', label: 'Product' },
    { value: 'qty', label: 'Qty' },
    { value: 'lineTotal', label: 'Line Total' },
  ],
  payments: [
    { value: 'fundingDate', label: 'Funding Date' },
    { value: 'transactionNumber', label: 'Transaction #' },
    { value: 'authCode', label: 'Auth Code' },
    { value: 'lastFour', label: 'Card' },
    { value: 'amount', label: 'Amount' },
    { value: 'status', label: 'Status' },
  ],
};

function SupernovaTable({
  tab,
  rows,
  isLoading,
  sortBy,
  sortDir,
  onSortChange,
  filters,
  onFilterChange,
}: {
  tab: SupernovaTab;
  rows: unknown[];
  isLoading: boolean;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSortChange: (field: string) => void;
  filters: Record<string, string>;
  onFilterChange: (field: string, value: string) => void;
}) {
  const columns = SUPERNOVA_SORT_OPTIONS[tab];
  const colCount = columns.length;

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
          {columns.map((col) => {
            const isSorted = sortBy === col.value;
            return (
              <th key={col.value} className="px-4 py-2 font-medium">
                <button
                  type="button"
                  onClick={() => onSortChange(col.value)}
                  className="flex items-center gap-1 whitespace-nowrap hover:text-foreground"
                >
                  {col.label}
                  {isSorted ? (
                    <ArrowUpDown className={cn('h-3 w-3 text-primary', sortDir === 'asc' && 'rotate-180')} />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-muted-foreground/40" />
                  )}
                </button>
              </th>
            );
          })}
        </tr>
        <tr className="border-b border-border bg-white">
          {columns.map((col) => (
            <th key={col.value} className="px-4 py-1.5 font-normal">
              <input
                type="text"
                value={filters[col.value] ?? ''}
                onChange={(e) => onFilterChange(col.value, e.target.value)}
                placeholder="Filter…"
                className="w-full min-w-[90px] rounded border border-input bg-white px-2 py-1 text-xs normal-case text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={colCount} className="px-4 py-10 text-center text-muted-foreground">
              Loading…
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={colCount}>
              <EmptyState icon={SearchX} title="No rows found" description="Try adjusting the filters above." />
            </td>
          </tr>
        ) : (
          rows.map((row) => <SupernovaTableRow key={JSON.stringify(row)} tab={tab} row={row} />)
        )}
      </tbody>
    </table>
  );
}

function SupernovaTableRow({ tab, row }: { tab: SupernovaTab; row: unknown }) {
  switch (tab) {
    case 'matched': {
      const r = row as SupernovaMatchedRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatDateTime(r.createdAt)}</td>
          <td className="px-4 py-3">{r.dailyOrderNumber ?? ''}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.transactionId}</td>
          <td className="px-4 py-3 font-mono">{r.authCode ?? ''}</td>
          <td className="px-4 py-3">{r.cardLastFour ? `**** ${r.cardLastFour}` : ''}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.paymentAmount)}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.fundingAmount)}</td>
          <td className="px-4 py-3">{formatDateTime(r.fundingDate)}</td>
        </tr>
      );
    }
    case 'unmatched-source': {
      const r = row as SupernovaUnmatchedSourceRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatDateTime(r.createdAt)}</td>
          <td className="px-4 py-3">{r.dailyOrderNumber ?? ''}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.transactionId}</td>
          <td className="px-4 py-3 font-mono">{r.authCode ?? ''}</td>
          <td className="px-4 py-3">{r.cardLastFour ? `**** ${r.cardLastFour}` : ''}</td>
          <td className="px-4 py-3 text-right">{r.itemCount}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.paymentAmount)}</td>
        </tr>
      );
    }
    case 'unmatched-funding': {
      const r = row as SupernovaUnmatchedFundingRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatDateTime(r.fundingDate)}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.transactionNumber}</td>
          <td className="px-4 py-3 font-mono">{r.authCode}</td>
          <td className="px-4 py-3">{r.lastFour ? `**** ${r.lastFour}` : ''}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.amount)}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.orderId ?? ''}</td>
        </tr>
      );
    }
    case 'amount-mismatch': {
      const r = row as SupernovaMatchedRow;
      const variance = parseFloat(r.paymentAmount) - parseFloat(r.fundingAmount);
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatDateTime(r.createdAt)}</td>
          <td className="px-4 py-3">{r.dailyOrderNumber ?? ''}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.transactionId}</td>
          <td className="px-4 py-3 font-mono">{r.authCode ?? ''}</td>
          <td className="px-4 py-3">{r.cardLastFour ? `**** ${r.cardLastFour}` : ''}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.paymentAmount)}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.fundingAmount)}</td>
          <td className="px-4 py-3 text-right font-semibold text-amber-600">{formatCurrency(variance)}</td>
        </tr>
      );
    }
    case 'orders':
    case 'excluded':
    case 'voided':
    case 'voided-recaptured': {
      const r = row as SupernovaOrderRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatDateTime(r.createdAt)}</td>
          <td className="px-4 py-3">{r.dailyOrderNumber ?? ''}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.id}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.total)}</td>
          <td className="px-4 py-3">
            <StatusBadge status={r.status} />
          </td>
        </tr>
      );
    }
    case 'duplicate-transactions': {
      const r = row as SupernovaDuplicatePaymentRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatDateTime(r.createdAt)}</td>
          <td className="px-4 py-3">{r.dailyOrderNumber ?? ''}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.transactionId}</td>
          <td className="px-4 py-3 font-mono">{r.authCode ?? ''}</td>
          <td className="px-4 py-3">{r.cardLastFour ? `**** ${r.cardLastFour}` : ''}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.amount)}</td>
        </tr>
      );
    }
    case 'items': {
      const r = row as SupernovaItemRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatDateTime(r.createdAt)}</td>
          <td className="px-4 py-3">{r.dailyOrderNumber ?? ''}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.transactionId}</td>
          <td className="px-4 py-3">{r.productId}</td>
          <td className="px-4 py-3 text-right">{r.qty}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.lineTotal)}</td>
        </tr>
      );
    }
    case 'payments': {
      const r = row as SupernovaFundedRow;
      return (
        <tr className="border-b border-border last:border-0">
          <td className="px-4 py-3">{formatDateTime(r.fundingDate)}</td>
          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.transactionNumber}</td>
          <td className="px-4 py-3 font-mono">{r.authCode}</td>
          <td className="px-4 py-3">{r.lastFour ? `**** ${r.lastFour}` : ''}</td>
          <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.amount)}</td>
          <td className="px-4 py-3">{r.status ? <StatusBadge status={r.status} /> : ''}</td>
        </tr>
      );
    }
    default:
      return null;
  }
}

export function SupernovaReconciliationPanel() {
  const { selectedLocationId } = useLocationStore();
  // The zone the DatePicker renders in; the date bridge must use the same one.
  const { timeZone: reportTz } = useReportingTimeZone();

  // Card reconciliation opens on a default range (1st of the month → today − 4
  // days) so records are on screen without the user touching the pickers. Both
  // the inputs and `applied` are seeded, so the first query already carries the
  // dates — no Apply click needed. Resolved once per mount via the initializer.
  const [defaultRange] = useState(() => defaultCardReconciliationRange(reportTz));

  const [activeTab, setActiveTab] = useState<SupernovaTab>('unmatched-source');
  const [dateFromInput, setDateFromInput] = useState(defaultRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(defaultRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>(defaultRange);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortBy, setSortBy] = useState(SUPERNOVA_SORT_OPTIONS['unmatched-source'][0].value);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedFilters(columnFilters);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [columnFilters]);

  const baseParams = {
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
  };
  const filtersParam = Object.keys(appliedFilters).length > 0 ? JSON.stringify(appliedFilters) : undefined;
  const listParams = { ...baseParams, page, perPage, sortBy, sortDir, filters: filtersParam };

  const { data: summaryData } = useSupernovaSummary(baseParams);
  const summary = summaryData?.data;

  const matchedQuery = useSupernovaMatched(listParams, activeTab === 'matched');
  const unmatchedSourceQuery = useSupernovaUnmatchedSource(listParams, activeTab === 'unmatched-source');
  const unmatchedFundingQuery = useSupernovaUnmatchedFunding(listParams, activeTab === 'unmatched-funding');
  const amountMismatchQuery = useSupernovaAmountMismatch(listParams, activeTab === 'amount-mismatch');
  const duplicateQuery = useSupernovaDuplicateTransactions(listParams, activeTab === 'duplicate-transactions');
  const ordersQuery = useSupernovaOrders(listParams, activeTab === 'orders');
  const excludedQuery = useSupernovaExcluded(listParams, activeTab === 'excluded');
  const voidedQuery = useSupernovaVoided(listParams, activeTab === 'voided');
  const voidedRecapturedQuery = useSupernovaVoidedRecaptured(listParams, activeTab === 'voided-recaptured');
  const itemsQuery = useSupernovaItems(listParams, activeTab === 'items');
  const paymentsQuery = useSupernovaPayments(listParams, activeTab === 'payments');

  interface ActiveQueryShape {
    data?: { data: unknown[]; pagination: SupernovaPagination };
    isLoading: boolean;
  }
  const queriesByTab: Record<SupernovaTab, ActiveQueryShape> = {
    matched: matchedQuery,
    'unmatched-source': unmatchedSourceQuery,
    'unmatched-funding': unmatchedFundingQuery,
    'amount-mismatch': amountMismatchQuery,
    'duplicate-transactions': duplicateQuery,
    orders: ordersQuery,
    excluded: excludedQuery,
    voided: voidedQuery,
    'voided-recaptured': voidedRecapturedQuery,
    items: itemsQuery,
    payments: paymentsQuery,
  };
  const activeQuery = queriesByTab[activeTab];
  const rows = activeQuery.data?.data ?? [];
  const pagination = activeQuery.data?.pagination;
  const activeTabDef = SUPERNOVA_TABS.find((t) => t.id === activeTab);

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    // Back to the page's opening state, i.e. the default range — clearing to
    // blank would leave no way to get the default back without a reload.
    setDateFromInput(defaultRange.dateFrom);
    setDateToInput(defaultRange.dateTo);
    setApplied(defaultRange);
    setColumnFilters({});
    setAppliedFilters({});
    setSortBy(SUPERNOVA_SORT_OPTIONS[activeTab][0].value);
    setSortDir('desc');
    setPage(1);
  };
  const handleTabChange = (tab: SupernovaTab) => {
    setActiveTab(tab);
    setPage(1);
    setSortBy(SUPERNOVA_SORT_OPTIONS[tab][0].value);
    setSortDir('desc');
    setColumnFilters({});
    setAppliedFilters({});
  };
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };
  const handleFilterChange = (field: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-5">
          <SupernovaSummaryStat
            label="Unmatched in Source"
            count={summary?.unmatchedSource.count}
            total={summary?.unmatchedSource.total}
          />
          <SupernovaSummaryStat
            label="Unmatched in Funded"
            count={summary?.unmatchedFunding.count}
            total={summary?.unmatchedFunding.total}
          />
          <SupernovaSummaryStat
            label="Amount Mismatch"
            count={summary?.amountMismatch.count}
            total={summary?.amountMismatch.total}
            accent
          />
          <SupernovaSummaryStat
            label="Duplicates"
            count={summary?.duplicates.count}
            total={summary?.duplicates.total}
            accent
          />
          <SupernovaSummaryStat label="Matched" count={summary?.matched.count} total={summary?.matched.total} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <DatePicker
          mode="date"
          timezone={reportTz}
          className="w-44"
          placeholder="Start date"
          value={ymdToDate(dateFromInput, reportTz)}
          onChange={(v) => setDateFromInput(dateToYmd(v as Date | null, reportTz))}
          maxDate={ymdToDate(dateToInput, reportTz) ?? undefined}
        />
        <DatePicker
          mode="date"
          timezone={reportTz}
          className="w-44"
          placeholder="End date"
          value={ymdToDate(dateToInput, reportTz)}
          onChange={(v) => setDateToInput(dateToYmd(v as Date | null, reportTz))}
          minDate={ymdToDate(dateFromInput, reportTz) ?? undefined}
        />
        <Button size="sm" variant="primary" onClick={handleApply}>
          Apply
        </Button>
        <Button size="sm" variant="destructive-outline" onClick={handleReset}>
          Reset
        </Button>
        <SearchableSelect
          className="w-72"
          clearable={false}
          value={activeTab}
          onChange={(v) => handleTabChange(v as SupernovaTab)}
          options={SUPERNOVA_TABS.map((t) => ({ value: t.id, label: t.label }))}
          placeholder="Select a view"
          searchPlaceholder="Find a view..."
        />
      </div>

      {activeTabDef?.help && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span>
            <span className="font-medium text-foreground">{activeTabDef.label}:</span> {activeTabDef.help}
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <SupernovaTable
            tab={activeTab}
            rows={rows}
            isLoading={activeQuery.isLoading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            filters={columnFilters}
            onFilterChange={handleFilterChange}
          />
        </div>
        {pagination && pagination.total > 0 && (
          <DataTablePagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            perPage={pagination.perPage}
            onPageChange={setPage}
            perPageOptions={[10, 25, 50, 100]}
            onPerPageChange={(val) => {
              setPerPage(val);
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
}
